import { AccountAttachmentRequirement } from "models/Account";
import React, { useEffect, useState } from "react";
import { Alert, Button, Card, CardBody, Input, Spinner } from "reactstrap";
import { getAttachmentRequirements, putAttachmentRequirements } from "services/Api";
import CardHeader from "components/CardHeader";
import { useOfficeTranslation } from "i18n/OfficeTranslation";

const emptyRequirement = (tenantId: string): AccountAttachmentRequirement => ({
    id: "",
    tenant: tenantId,
    name: "",
    description: "",
    kind: "upload",
    declarations: [],
    requires_expiry: true,
    required_for_hours: true,
});

const TenantAttachmentRequirements: React.FC<{ tenantId: string }> = ({ tenantId }) => {
    const { t } = useOfficeTranslation();
    const [requirements, setRequirements] = useState<AccountAttachmentRequirement[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<{ color: string; text: string } | null>(null);

    useEffect(() => {
        setLoading(true);
        getAttachmentRequirements(tenantId)
            .then(setRequirements)
            .catch(() => setMessage({ color: "danger", text: t("tenants.tenantAttachmentRequirements.attachmentRequirementsCouldNotBeLoaded") }))
            .finally(() => setLoading(false));
    }, [t, tenantId]);

    const update = (index: number, patch: Partial<AccountAttachmentRequirement>) => {
        setRequirements(values => values.map((value, current) => current === index ? { ...value, ...patch } : value));
    };

    const save = async () => {
        setSaving(true);
        setMessage(null);
        try {
            const saved = await putAttachmentRequirements(tenantId, requirements);
            setRequirements(saved);
            setMessage({ color: "success", text: t("tenants.tenantAttachmentRequirements.attachmentRequirementsSaved") });
        } catch (reason: any) {
            setMessage({ color: "danger", text: String(reason.response?.data?.error ?? reason) });
        } finally {
            setSaving(false);
        }
    };

    return <Card>
        <CardBody>
            <CardHeader
                title={t("tenants.tenantAttachmentRequirements.title")}
                icon="mdi-file-check-outline"
                count={requirements.length}
                actions={
                    <Button size="sm" color="light" onClick={() => setRequirements(values => [...values, emptyRequirement(tenantId)])}>
                        <i className="mdi mdi-plus me-1" />{t("tenants.tenantAttachmentRequirements.add")}
                    </Button>
                }
            />
            <p className="text-muted mt-3 mb-0">
                {t("tenants.tenantAttachmentRequirements.chooseWhichApprovedDocumentsWorkersNeedBeforeTheyCanEnterHours")}
            </p>
            {message && <Alert className="mt-3 mb-0" color={message.color}>{message.text}</Alert>}
            {loading ? <div className="pt-3"><Spinner size="sm" /></div> : requirements.length === 0
                ? <p className="text-muted mt-3 mb-0">{t("tenants.tenantAttachmentRequirements.noDocumentTypesConfigured")}</p>
                : requirements.map((requirement, index) => <div key={requirement.id || `new-${index}`} className="border-bottom py-3">
                    <div className="d-flex gap-2">
                        <Input
                            aria-label={t("tenants.tenantAttachmentRequirements.attachmentTypeName")}
                            placeholder={t("tenants.tenantAttachmentRequirements.forExampleChamberOfCommerceExtract")}
                            value={requirement.name}
                            onChange={event => update(index, { name: event.target.value })}
                        />
                        <Button
                            color="outline-danger"
                            aria-label={t("tenants.tenantAttachmentRequirements.removeAttachmentType")}
                            onClick={() => setRequirements(values => values.filter((_, current) => current !== index))}
                        >
                            <i className="mdi mdi-delete-outline" />
                        </Button>
                    </div>
                    <Input
                        className="mt-2"
                        aria-label={t("tenants.tenantAttachmentRequirements.attachmentTypeDescription")}
                        placeholder={t("tenants.tenantAttachmentRequirements.instructionsShownInTheCustomerPortal")}
                        value={requirement.description ?? ""}
                        onChange={event => update(index, { description: event.target.value })}
                    />
                    <div className="row g-2 mt-1">
                        <div className="col-md-4">
                            <label className="form-label small mb-1">
                                {t("tenants.tenantAttachmentRequirements.inputMethod")}
                            </label>
                            <Input
                                type="select"
                                value={requirement.kind || "upload"}
                                onChange={event => update(index, {
                                    kind: event.target.value as AccountAttachmentRequirement["kind"],
                                    requires_expiry: event.target.value === "form" ? true : requirement.requires_expiry,
                                })}
                            >
                                <option value="upload">{t("tenants.tenantAttachmentRequirements.uploadDocument")}</option>
                                <option value="form">{t("tenants.tenantAttachmentRequirements.completeForm")}</option>
                            </Input>
                        </div>
                        {(requirement.kind || "upload") === "form" && <div className="col-md-8">
                            <label className="form-label small mb-1">
                                {t("tenants.tenantAttachmentRequirements.declarations")}
                            </label>
                            <Input
                                type="textarea"
                                rows={4}
                                value={(requirement.declarations ?? []).join("\n")}
                                placeholder={t("tenants.tenantAttachmentRequirements.oneDeclarationPerLine")}
                                onChange={event => update(index, {
                                    declarations: event.target.value.split("\n"),
                                })}
                            />
                        </div>}
                    </div>
                    <div className="d-flex flex-wrap gap-4 mt-3">
                        <label className="form-check-label">
                            <Input type="checkbox" className="me-2" checked={requirement.requires_expiry}
                                disabled={(requirement.kind || "upload") === "form"}
                                onChange={event => update(index, { requires_expiry: event.target.checked })} />
                            {t("tenants.tenantAttachmentRequirements.expiryDateRequired")}
                        </label>
                        <label className="form-check-label">
                            <Input type="checkbox" className="me-2" checked={requirement.required_for_hours}
                                onChange={event => update(index, { required_for_hours: event.target.checked })} />
                            {t("tenants.tenantAttachmentRequirements.requiredForHourRegistration")}
                        </label>
                    </div>
                </div>)}
            <div className="text-end mt-3">
                <Button color="primary" disabled={saving || requirements.some(value =>
                    !value.name.trim() || ((value.kind || "upload") === "form" && !(value.declarations ?? []).some(item => item.trim()))
                )} onClick={save}>
                    {saving ? <Spinner size="sm" /> : t("tenants.tenantAttachmentRequirements.saveRequirements")}
                </Button>
            </div>
        </CardBody>
    </Card>;
};

export default TenantAttachmentRequirements;
